import React, { useState } from 'react';
import { useCompany } from '../contexts/CompanyContext';
import type { Company } from '../contexts/CompanyContext';
import { Building2, X, Search, Check } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';

interface CompanySwitcherProps {
  isOpen: boolean;
  onClose: () => void;
}

const srOnlyStyle: React.CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

const CompanySwitcher: React.FC<CompanySwitcherProps> = ({ isOpen, onClose }) => {
  const { t } = useI18n();
  const { currentCompany, companies, setCurrentCompany, isLoading } = useCompany();
  const [searchTerm, setSearchTerm] = useState('');

  if (!isOpen) return null;

  const filteredCompanies = (Array.isArray(companies) ? companies : []).filter(company =>
    company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    company.document?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectCompany = async (company: Company) => {
    try {
      await setCurrentCompany(company);
      onClose();
      setSearchTerm('');
      // Nota: Troca de empresa pode requerer novo login
      console.info('[CompanySwitcher] Empresa alterada para:', company.name);
    } catch (error) {
      console.error('Erro ao trocar empresa:', error);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        padding: '20px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '500px',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Building2 size={24} style={{ color: '#2563eb' }} />
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#111827' }}>{t('company.switchTitle')}</h2>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X size={20} style={{ color: '#6b7280' }} />
          </button>
        </div>

        {/* Search */}
        <div style={{ padding: '16px', borderBottom: '1px solid #e5e7eb', position: 'relative' }}>
          <label htmlFor="company-switcher-search" style={srOnlyStyle}>{t('company.search')}</label>
          <div style={{ position: 'relative' }}>
            <Search
              size={18}
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#9ca3af',
              }}
            />
            <input
              id="company-switcher-search"
              name="company-search"
              type="text"
              placeholder={t('company.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px 10px 40px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
              }}
              autoFocus
            />
          </div>
        </div>

        {/* Companies List */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
          {isLoading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
              {t('companySwitcher.loading')}
            </div>
          ) : filteredCompanies.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
              {searchTerm ? t('companySwitcher.notFound') : t('companySwitcher.noneAvailable')}
            </div>
          ) : (
            filteredCompanies.map((company) => (
              <button
                key={company.id}
                onClick={() => handleSelectCompany(company)}
                style={{
                  width: '100%',
                  padding: '16px',
                  textAlign: 'left',
                  border: 'none',
                  backgroundColor: company.id === currentCompany?.id ? '#eff6ff' : 'transparent',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '4px',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => {
                  if (company.id !== currentCompany?.id) {
                    e.currentTarget.style.backgroundColor = '#f9fafb';
                  }
                }}
                onMouseLeave={(e) => {
                  if (company.id !== currentCompany?.id) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontWeight: '500',
                      fontSize: '16px',
                      color: '#111827',
                      marginBottom: '4px',
                    }}
                  >
                    {company.name}
                  </div>
                  {company.document && (
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>
                      {company.document}
                    </div>
                  )}
                  {company.email && (
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>
                      {company.email}
                    </div>
                  )}
                </div>
                {company.id === currentCompany?.id && (
                  <Check size={20} style={{ color: '#2563eb' }} />
                )}
              </button>
            ))
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '16px',
            borderTop: '1px solid #e5e7eb',
            backgroundColor: '#f9fafb',
            borderRadius: '0 0 12px 12px',
          }}
        >
          <p style={{ margin: 0, fontSize: '12px', color: '#6b7280', textAlign: 'center' }}>{t('company.switchHint')}</p>
        </div>
      </div>
    </div>
  );
};

export default CompanySwitcher;

